import './instrument'
import * as Sentry from '@sentry/react'
import './reset.css'
import 'antd/dist/antd.css'
import React from 'react'
import ReactDOM from 'react-dom'
import reportWebVitals from './reportWebVitals'
import { HashRouter, BrowserRouter } from 'react-router-dom'
import { AuthProvider } from 'context/AuthContext'
import { ApolloClient, InMemoryCache } from '@apollo/client'
import { ApolloProvider } from '@apollo/client/react'
import { App } from './App'
import { NODE_ENV, REACT_APP_BACK_END_URL } from 'config'
import { Result } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import CIMSButton from 'components/CIMSButton'

const client = new ApolloClient({
  uri: `${NODE_ENV === 'production' ? REACT_APP_BACK_END_URL : ''}/graphql`,
  credentials: 'include',
  cache: new InMemoryCache({
    typePolicies: {
      ConcreteTest: { keyFields: ['globalId'] },
      PacketFile: { keyFields: ['sharePointItemId'] },
    },
  }),
})

const Router: React.FC = ({ children }) => {
  if (NODE_ENV === 'production')
    return <BrowserRouter>{children}</BrowserRouter>
  return <HashRouter>{children}</HashRouter>
}

/**
 * Formats ErrorBoundary context for users to copy and send to their CIMS point
 * of contact when an unrecoverable client-side render error occurs.
 */
function getErrorBoundaryDetails({
  componentStack,
  error,
  eventId,
}: {
  componentStack: string
  error: unknown
  eventId: string
}) {
  const normalizedError =
    error instanceof Error
      ? {
          message: error.message,
          name: error.name,
          stack: error.stack,
        }
      : error

  return JSON.stringify(
    {
      componentStack,
      error: normalizedError,
      eventId,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    },
    null,
    2
  )
}

ReactDOM.render(
  <React.StrictMode>
    <ApolloProvider client={client}>
      <Router>
        <AuthProvider>
          <Sentry.ErrorBoundary
            fallback={({ componentStack, error, eventId }) => (
              <Result
                status="500"
                title="Unexpected Error"
                subTitle="Refresh the page and try again. If this keeps happening, contact your CIMS point of contact."
                extra={
                  <>
                    <CIMSButton
                      type="primary"
                      onClick={() => window.location.reload()}
                    >
                      Refresh Page
                    </CIMSButton>
                    <CIMSButton
                      icon={<CopyOutlined />}
                      disabled={{
                        disabled:
                          typeof navigator.clipboard?.writeText !== 'function',
                        reason: {
                          why: 'disabled',
                          message:
                            'Copying error details is unavailable in this browser or security context',
                        },
                      }}
                      onClick={() => {
                        void navigator.clipboard
                          .writeText(
                            getErrorBoundaryDetails({
                              componentStack,
                              error,
                              eventId,
                            })
                          )
                          .catch((error) => console.error(error))
                      }}
                    >
                      Copy Error Details
                    </CIMSButton>
                  </>
                }
              />
            )}
            onError={(error) => console.error(error)}
          >
            <App />
          </Sentry.ErrorBoundary>
        </AuthProvider>
      </Router>
    </ApolloProvider>
  </React.StrictMode>,
  document.getElementById('root')
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
